import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faTrashCan } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faPause, faPlay } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { WithReferralAddress } from '@soundxyz/utils/constants/embeds';
import { styled, theme } from '@components-shared/src/stitches';
import { useLikeRelease } from '@components/Likes/useLikeRelease';

import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { LikeSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/LikeSoundMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { ListeningPartyTooltip } from '@components/main-components/play-button/PlayButton';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { ParamsStore } from '@context/params';
import { useCurrentListeningParty } from '@hooks/usePlayButton';
import { usePrefetchTrackInView } from '@hooks/usePrefetchTracksInView';
import { loadActiveTrack } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { removeTrackAtPosition, setActiveTrack } from '@store/audio/AudioQueue';
import { KeepCurrentBrowserHost } from '@utils/url';
import { SongTitle } from './SongTitle';

const IMAGE_SIZE = 48;
export const ITEM_HEIGHT = 64;

export const QueueListItem = ({
  activeTrackIndex,
  deleteEnabled = false,
  index,
  trackId,
  playing = false,
  scrollToTop,
  isScrolling,
}: {
  activeTrackIndex: number;
  deleteEnabled?: boolean;
  index: number;
  trackId: string;
  playing: boolean;
  scrollToTop: () => void;
  isScrolling: boolean;
}) => {
  const { inViewRef, playerTrackInfo } = usePrefetchTrackInView({
    trackId,
    shouldFetch: !isScrolling,
  });

  const releaseInfo = playerTrackInfo.releaseInfo;

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const staticCoverImageUrl = releaseInfo?.staticCoverImage?.url;
  const coverImageUrl = releaseInfo?.coverImage.url ?? '';
  const artistName = releaseInfo?.artist.name ?? 'Unnamed Artist';
  const songTitle = releaseInfo?.title ?? 'Unnamed Song';

  const isActive = index === 0;
  const playbackIndex = index + activeTrackIndex;

  const { isLiked, onClick: onLikeClick } = useLikeRelease({
    id: releaseInfo?.id,
    source: 'Audio Player',
  });

  const currentListeningParty = useCurrentListeningParty({
    listeningParty: releaseInfo?.listeningParty,
    releaseId: releaseInfo?.id,
  });

  if (!releaseInfo) {
    return (
      <Container ref={inViewRef} isActive={isActive}>
        {!isActive && <Index>{index}</Index>}
        <ImageSkeleton />
        <FullSkeleton />
      </Container>
    );
  }

  const disabled = !!currentListeningParty;

  const handleClick = () => {
    if (isActive) {
      togglePlayPause();
    } else {
      setActiveTrack(playbackIndex);
      loadActiveTrack();
      scrollToTop();
    }
  };

  const webappUri = KeepCurrentBrowserHost(
    WithReferralAddress(releaseInfo.webappUri, {
      referralAddress: ParamsStore.referral,
      source: {
        link: true,
      },
    }),
  ).href;

  const imagePlayButton = (
    <ImageContainer
      onClick={handleClick}
      disabled={disabled}
      className={
        currentListeningParty ? 'transition-all duration-500 hover:brightness-[80%]' : undefined
      }
    >
      <NextFutureImage
        placeholder="blur"
        src={staticCoverImageUrl || coverImageUrl}
        blurDataURL={blurImage(IMAGE_SIZE, IMAGE_SIZE)}
        alt={songTitle}
        height={IMAGE_SIZE * 2}
        width={IMAGE_SIZE * 2}
      />
      <HoverIcon>
        <FontAwesomeIcon
          icon={playing ? faPause : faPlay}
          color={theme.colors.neutral800.toString()}
          className={currentListeningParty ? 'opacity-50' : undefined}
        />
      </HoverIcon>
    </ImageContainer>
  );

  return (
    <Container ref={inViewRef} isActive={isActive}>
      {!isActive && <Index>{index}</Index>}
      {currentListeningParty != null ? (
        <ListeningPartyTooltip listeningParty={currentListeningParty}>
          {imagePlayButton}
        </ListeningPartyTooltip>
      ) : (
        imagePlayButton
      )}
      <InfoContainer disabled={disabled} onClick={handleClick}>
        <SongTitle songTitle={songTitle} webappUri={webappUri} />
        <ArtistName>{artistName}</ArtistName>
      </InfoContainer>
      <ActionsContainer>
        {isActive && releaseInfo && (
          <ThreeDotsDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            dropdownProps={{
              queue: true,
              align: 'end',
            }}
          >
            <LikeSoundMenuItem liked={isLiked} onClick={onLikeClick} data={releaseInfo} />
            <AddToShelfMenuItem releaseId={releaseInfo.id} source="Audio Player" />
            <ShareMenuItem releaseId={releaseInfo.id} source="Audio Player" />
          </ThreeDotsDropdown>
        )}
        {deleteEnabled && (
          <IconButton
            icon={<FontAwesomeIcon icon={faTrashCan} size="lg" />}
            onClick={() => {
              removeTrackAtPosition(playbackIndex);
            }}
            title="Remove"
            size={24}
            iconSize={14}
          />
        )}
      </ActionsContainer>
    </Container>
  );
};

const HoverIcon = styled('div', {
  backgroundColor: '$neutral100',
  borderRadius: 6,
  bottom: 2,
  opacity: 0,
  position: 'absolute',
  right: 2,
  transition: 'opacity 0.25s linear',
  height: 24,
  width: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const Container = styled('li', {
  alignItems: 'center',
  borderRadius: 8,
  display: 'flex',
  padding: 8,
  height: ITEM_HEIGHT,
  variants: {
    isActive: {
      true: {
        backgroundColor: '$base200',
      },
    },
  },
  '&:not(:first-child)': {
    marginTop: 8,
  },
  [`&:hover ${HoverIcon}`]: {
    opacity: 1,
  },
});

const Index = styled('span', {
  base: 'S',
  color: '$neutral900',
  flexShrink: 0,
  marginRight: 8,
  minWidth: 16,
});

const ImageContainer = styled('button', {
  flexShrink: 0,
  height: IMAGE_SIZE,
  width: IMAGE_SIZE,
  overflow: 'hidden',
  borderRadius: 6,
  marginRight: 16,
  cursor: 'pointer',
  position: 'relative',
  [`&:focus-visible ${HoverIcon}`]: {
    opacity: 1,
  },
});

const InfoContainer = styled('button', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  justifyContent: 'center',
  overflow: 'hidden',
  cursor: 'pointer',
});

const ArtistName = styled('span', {
  base: 'S',
  color: '$neutral600',
});

const ActionsContainer = styled('div', {
  alignItems: 'end',
  width: 40,
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 16,
  height: '100%',
  base: 'S',
  color: '$neutral600',
  paddingTop: 8,
  paddingRight: 4,
});

const ImageSkeleton = styled(Skeleton, {
  height: IMAGE_SIZE,
  width: IMAGE_SIZE,
  borderRadius: 6,
  flexShrink: 0,
  marginRight: 16,
});

const FullSkeleton = styled(Skeleton, {
  height: '100%',
  width: '100%',
  borderRadius: 6,
});
